import React, {useCallback, useMemo, useState} from 'react';

import {useMutation, useQuery} from '@apollo/client/react';
import {AlertCircle, AlertTriangle, ChevronDown, Info, List, Plus, Tag, Trash2, X} from 'lucide-react';
import {SeatUsage, SubscriptionOfferLine, SubscriptionProduct} from '../../types/graphql';
import {Pill} from '../SubscriptionBadges';
import {DEFAULT_SEAT_TYPE, formatMoney, formatSeats, isInvoiced, todayLocalISO, toEpochEndOfDay, toNumber} from '../../utils/subscriptions';
import {
  describeOfferDiscount,
  formatOfferTotals,
  groupOfferProducts,
  lineListAmount,
  MAX_PAYMENT_TERMS_DAYS,
  MAX_TRIAL_DAYS,
  MIN_PAYMENT_TERMS_DAYS,
  MIN_TRIAL_DAYS,
  OfferTarget,
  offerTotals,
  prefillFromSubscription,
  seatShortfall,
  SUGGESTED_PAYMENT_TERMS_DAYS,
} from '../../utils/subscriptionOffers';
import {gql} from 'types/__generated__';
import ProductPicker, {productKind} from './ProductPicker';

/**
 * The staff catalog, not the customer one: `subscriptionProducts` lists only the ids configured
 * for self-service and hides managed products, which is why support plans and Enterprise tiers
 * never used to appear here. System admin only, like the mutation it feeds.
 */
export const OFFER_PRODUCTS_QUERY = gql(`
  query SubscriptionOfferProducts {
    offerProducts {
      id
      name
      description
      module
      tier
      addon
      seatType
      isManaged
      quantity
      metadata {
        key
        value
      }
      price {
        amount
        currency
      }
    }
  }
`);

export const CREATE_SUBSCRIPTION_OFFER = gql(`
  mutation CreateSubscriptionOffer($organisationId: UUID!, $input: CreateSubscriptionOfferInput!) {
    createSubscriptionOffer(organisationId: $organisationId, input: $input)
  }
`);

interface SubscriptionOfferModalProps {
  organisationId: string;
  organisationName: string;
  onClose: () => void;
  /** Called after the offer exists, so the caller can refetch its history. */
  onCreated: () => void;
  /**
   * The paid subscription the offer changes on acceptance, or null when it creates one. The
   * builder starts from its lines and discounts: what sales submits is the whole new subscription.
   */
  target?: OfferTarget | null;
  /** Seat usage today, to warn when the offer seats fewer members than are assigned. */
  seatUsage?: SeatUsage | null;
}

interface LineDraft {
  /** Stable across re-orders; product ids repeat only long enough to be rejected. */
  key: string;
  productId: string;
  quantity: string;
  discountPercent: string;
  discountMonths: string;
  /** Typed by id rather than picked: a managed product, or one the catalog does not list. */
  manual: boolean;
}

const inputClass =
  'mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
const cellInputClass =
  'w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm';
const primaryButtonClass =
  'px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors';
const subtleButtonClass =
  'px-3 py-1.5 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors';

let nextLineKey = 0;

function newLine(productId = '', manual = false): LineDraft {
  nextLineKey += 1;
  return {key: `line-${nextLineKey}`, productId, quantity: '1', discountPercent: '', discountMonths: '', manual};
}

/** A number as the field shows it: a BigDecimal string without trailing zeros, or empty for none. */
function fieldValue(value: number | string | null | undefined): string {
  const numeric = toNumber(value);
  return numeric === null ? '' : String(Number(numeric.toFixed(2)));
}

/**
 * Every line of the running subscription, with its discount restated as the months it has left,
 * so an offer that changes nothing but the terms carries the deal over knowingly. The offer's
 * discounts replace the subscription's on acceptance; a line left out is a line removed.
 */
function initialLines(target: OfferTarget | null | undefined): LineDraft[] {
  if (!target) return [];
  const prefill = prefillFromSubscription(target);
  return prefill.lines.map((line) => ({
    ...newLine(line.productId),
    quantity: String(line.quantity),
    discountPercent: fieldValue(line.discount?.percentOff),
    discountMonths: fieldValue(line.discount?.durationMonths),
  }));
}

/** Trim to a number, or null for an empty field. NaN means "typed, but not a number". */
function parseField(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return Number(trimmed);
}

function discountInput(percent: string, months: string): {percentOff: number; durationMonths: number | null} | null {
  const percentValue = parseField(percent);
  if (percentValue === null) return null;
  const monthsValue = parseField(months);
  return {percentOff: percentValue, durationMonths: monthsValue};
}

/**
 * The same rules the server enforces, checked here so sales sees the problem next to the field
 * instead of as a round-trip error. The server remains the authority — its messages are shown
 * verbatim when something slips through (an unknown product id, a price that vanished).
 */
function validateDiscount(percent: string, months: string, label: string): string | null {
  const percentValue = parseField(percent);
  const monthsValue = parseField(months);
  if (percentValue === null) {
    return monthsValue === null ? null : `${label}: a duration needs a percentage to go with it`;
  }
  if (!Number.isFinite(percentValue) || percentValue <= 0 || percentValue > 100) {
    return `${label}: discount must be above 0 and at most 100 percent`;
  }
  if (monthsValue !== null && (!Number.isInteger(monthsValue) || monthsValue < 1)) {
    return `${label}: discount duration must be at least one month`;
  }
  return null;
}

export default function SubscriptionOfferModal({
  organisationId,
  organisationName,
  onClose,
  onCreated,
  target = null,
  seatUsage = null,
}: SubscriptionOfferModalProps) {
  // Read once, for the initial state only: the form is sales' draft from here on.
  const startingGlobal = target ? prefillFromSubscription(target).globalDiscount : null;
  const [lines, setLines] = useState<LineDraft[]>(() => initialLines(target));
  const [globalPercent, setGlobalPercent] = useState(() => fieldValue(startingGlobal?.percentOff));
  const [globalMonths, setGlobalMonths] = useState(() => fieldValue(startingGlobal?.durationMonths));
  const [trialDays, setTrialDays] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  /** The open product picker: which line it changes, or null when it adds a line. */
  const [picker, setPicker] = useState<{anchor: HTMLElement; lineKey: string | null} | null>(null);

  const {data: productsData, error: productsError} = useQuery<{offerProducts: SubscriptionProduct[]}>(OFFER_PRODUCTS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [createOffer, {loading: creating}] = useMutation(CREATE_SUBSCRIPTION_OFFER);

  const catalog = productsData?.offerProducts ?? [];
  const catalogById = useMemo(() => new Map(catalog.map((product) => [product.id, product])), [catalog]);
  /**
   * Managed products lock the resulting subscription to staff changes, so they are not something
   * to pick by accident: the picker lists them only once sales switches them on, and marks them.
   * They are always in `catalogById`, so a pasted id still resolves to its name, price and warning.
   */
  const [showManaged, setShowManaged] = useState(false);
  const managedCount = useMemo(() => catalog.filter((product) => product.isManaged).length, [catalog]);
  const productGroups = useMemo(
    () => groupOfferProducts(showManaged ? catalog : catalog.filter((product) => !product.isManaged)),
    [catalog, showManaged],
  );
  /** The running subscription's own products, to name a prefilled line before the catalog loads or when it lacks one. */
  const targetProductsById = useMemo(() => new Map((target?.products ?? []).map((product) => [product.id, product])), [target]);

  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((line) => (line.key === key ? {...line, ...patch} : line)));

  const closePicker = useCallback(() => setPicker(null), []);

  /** Picking fills the line the picker was opened from, or adds a line when it came from "Add product". */
  const pickProduct = (product: SubscriptionProduct) => {
    if (picker?.lineKey) updateLine(picker.lineKey, {productId: product.id, manual: false});
    else setLines((current) => [...current, newLine(product.id)]);
    setPicker(null);
  };

  const pasteProductId = () => {
    if (picker?.lineKey) updateLine(picker.lineKey, {manual: true});
    else setLines((current) => [...current, newLine('', true)]);
    setPicker(null);
  };

  /**
   * Preview lines carry the catalog product when we know it. A pasted id for a product outside
   * the self-service catalog (a support plan, an overage unit) resolves only on the server, so
   * the preview shows the deal without a price rather than pretending the line is free.
   */
  const previewLines: SubscriptionOfferLine[] = lines
    .filter((line) => line.productId.trim() !== '')
    .map((line) => ({
      productId: line.productId.trim(),
      product: catalogById.get(line.productId.trim()) ?? null,
      quantity: parseField(line.quantity) ?? 1,
      discount: discountInput(line.discountPercent, line.discountMonths),
    }));

  const totals = offerTotals({lines: previewLines, globalDiscount: discountInput(globalPercent, globalMonths)});
  const globalDiscountText = describeOfferDiscount(discountInput(globalPercent, globalMonths));
  /**
   * Seats follow the offer: fewer than are assigned means the server trims on acceptance and
   * mails the owners who lost theirs. A warning, never a block — sales may mean it.
   */
  const losingSeats = target && seatUsage ? seatShortfall(previewLines, seatUsage.usedSeats) : 0;
  const seatWarning =
    losingSeats > 0 ? `${losingSeats} assigned ${losingSeats === 1 ? 'member' : 'members'} will lose their seat on acceptance` : null;

  /**
   * Terms turn the deal into an invoiced one: no Stripe page, billing details from the customer
   * on acceptance, and Stripe sending each invoice. Read once here so the summary, the warnings
   * and the mutation input all say the same thing.
   */
  const termsDays = parseField(paymentTermsDays);
  const isInvoicedOffer = termsDays !== null && Number.isFinite(termsDays);
  /**
   * Terms on an offer to an organisation already paying by card switch that subscription to
   * invoicing when accepted. One already invoiced is only having its terms restated, which is
   * not news; an offer without terms leaves the collection method alone either way.
   */
  const switchesToInvoicing = isInvoicedOffer && !!target && !isInvoiced(target);

  function validate(): string | null {
    const filled = lines.filter((line) => line.productId.trim() !== '');
    if (filled.length === 0) return 'Add at least one product to the offer';

    const seen = new Set<string>();
    for (const line of filled) {
      const productId = line.productId.trim();
      if (seen.has(productId)) return 'Duplicate products in offer lines';
      seen.add(productId);

      const quantity = parseField(line.quantity);
      if (quantity !== null && (!Number.isInteger(quantity) || quantity < 1)) return 'Offer line quantity must be at least 1';

      const discountError = validateDiscount(line.discountPercent, line.discountMonths, catalogById.get(productId)?.name || productId);
      if (discountError) return discountError;
    }

    const globalError = validateDiscount(globalPercent, globalMonths, 'Whole subscription');
    if (globalError) return globalError;

    const trial = parseField(trialDays);
    if (trial !== null && target) return 'A trial cannot be offered on an existing subscription; use a 100% discount for N months';
    if (trial !== null && (!Number.isInteger(trial) || trial < MIN_TRIAL_DAYS || trial > MAX_TRIAL_DAYS)) {
      return `Offer trial must be between ${MIN_TRIAL_DAYS} and ${MAX_TRIAL_DAYS} days`;
    }

    if (termsDays !== null && (!Number.isInteger(termsDays) || termsDays < MIN_PAYMENT_TERMS_DAYS || termsDays > MAX_PAYMENT_TERMS_DAYS)) {
      return `Payment terms must be between ${MIN_PAYMENT_TERMS_DAYS} and ${MAX_PAYMENT_TERMS_DAYS} days`;
    }

    if (validUntil.trim() !== '' && toEpochEndOfDay(validUntil) <= Date.now()) return 'Offer deadline must be in the future';

    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    const input = {
      lines: lines
        .filter((line) => line.productId.trim() !== '')
        .map((line) => ({
          productId: line.productId.trim(),
          quantity: parseField(line.quantity) ?? 1,
          discount: discountInput(line.discountPercent, line.discountMonths),
        })),
      globalDiscount: discountInput(globalPercent, globalMonths),
      trialDays: parseField(trialDays),
      paymentTermsDays: termsDays,
      validUntil: validUntil.trim() === '' ? null : toEpochEndOfDay(validUntil),
      note: note.trim() || null,
    };

    try {
      await createOffer({variables: {organisationId, input}});
    } catch (err: any) {
      setError(err.message || 'The offer could not be created');
      return;
    }
    onCreated();
    onClose();
  };

  return (
    // Above z-50: the builder can be opened from inside the subscription modal.
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Tag size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Make an offer</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {organisationName}
                  {target && ' — changes the current subscription'}
                </p>
              </div>
              {target && !target.autoRenewal && (
                <Pill tone="amber" title="Renewal is cancelled today; accepting the offer turns it back on">
                  retention: reinstates renewal
                </Pill>
              )}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {(error || productsError) && (
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                {error || `The product catalog could not be loaded: ${productsError?.message}`}
              </div>
            )}

            {target && (
              <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200">
                <Info size={16} className="mt-0.5 shrink-0" />
                <span>
                  Prefilled from the current subscription — what you submit is the <em>whole</em> new subscription. Lines left out are removed
                  (removing the seat line removes purchased seats), and the offer&rsquo;s discounts replace every existing one on acceptance, counting
                  from that day. The change is immediate and prorated,{' '}
                  {isInvoicedOffer ? 'and the proration is sent as an invoice rather than charged to a card.' : 'invoiced on the card on file.'}
                </span>
              </div>
            )}

            {/* Lines */}
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Products</h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2 font-medium">Product</th>
                      <th className="px-3 py-2 font-medium w-20 text-right">Qty</th>
                      <th className="px-3 py-2 font-medium w-24 text-right">% off</th>
                      <th className="px-3 py-2 font-medium w-24 text-right">Months</th>
                      <th className="px-3 py-2 font-medium w-32 text-right">List / month</th>
                      <th className="px-3 py-2 w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                    {lines.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                          No products yet — use &ldquo;Add product&rdquo; to pick from the catalog.
                        </td>
                      </tr>
                    )}
                    {lines.map((line) => {
                      const product = catalogById.get(line.productId.trim());
                      const shown = product ?? targetProductsById.get(line.productId.trim());
                      const listAmount = lineListAmount({
                        productId: line.productId,
                        product: product ?? null,
                        quantity: parseField(line.quantity) ?? 1,
                      });
                      return (
                        <tr key={line.key} className="align-top">
                          <td className="px-3 py-2 space-y-1">
                            {line.manual ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={line.productId}
                                  onChange={(event) => updateLine(line.key, {productId: event.target.value})}
                                  placeholder="e.g. cway_enhanced_support"
                                  aria-label="Product id"
                                  autoFocus
                                  className={`${cellInputClass} font-mono`}
                                />
                                <button
                                  type="button"
                                  onClick={(event) => setPicker({anchor: event.currentTarget, lineKey: line.key})}
                                  title="Choose from the catalog"
                                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                >
                                  <List size={16} />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(event) => {
                                  // Read now: React clears currentTarget before a state updater runs.
                                  const anchor = event.currentTarget;
                                  // A line that already carries a managed product has to find it in the list.
                                  if (product?.isManaged) setShowManaged(true);
                                  setPicker((current) => (current?.lineKey === line.key ? null : {anchor, lineKey: line.key}));
                                }}
                                aria-haspopup="dialog"
                                aria-expanded={picker?.lineKey === line.key}
                                className="group flex w-full items-center gap-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-left hover:border-blue-400 dark:hover:border-blue-500"
                              >
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {shown?.name || <span className="font-mono">{line.productId}</span>}
                                  </span>
                                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                    {shown ? productKind(shown) : 'Not in the catalog'}
                                  </span>
                                </span>
                                <ChevronDown size={14} className="shrink-0 text-gray-400 group-hover:text-blue-500" />
                              </button>
                            )}
                            {product?.isManaged && (
                              <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
                                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                Managed: the customer cannot add seats or cancel the subscription themselves.
                              </p>
                            )}
                            {/* seatUsage is the project block; a Lightbox seat line is counted separately. */}
                            {target && seatUsage && product?.seatType === DEFAULT_SEAT_TYPE && (
                              <p
                                className={`flex items-start gap-1 text-xs ${seatWarning ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}
                              >
                                {seatWarning && <AlertTriangle size={12} className="mt-0.5 shrink-0" />}
                                <span>
                                  In use today: {formatSeats(seatUsage.usedSeats, seatUsage.totalSeats)}
                                  {seatWarning && ` — ${seatWarning}`}
                                </span>
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min={1}
                              step={1}
                              value={line.quantity}
                              onChange={(event) => updateLine(line.key, {quantity: event.target.value})}
                              className={`${cellInputClass} text-right`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step="any"
                              value={line.discountPercent}
                              onChange={(event) => updateLine(line.key, {discountPercent: event.target.value})}
                              placeholder="—"
                              className={`${cellInputClass} text-right`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min={1}
                              step={1}
                              value={line.discountMonths}
                              onChange={(event) => updateLine(line.key, {discountMonths: event.target.value})}
                              placeholder="∞"
                              disabled={line.discountPercent.trim() === ''}
                              className={`${cellInputClass} text-right disabled:opacity-50`}
                            />
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {listAmount !== null && product ? formatMoney(listAmount, product.price.currency) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => setLines((current) => current.filter((row) => row.key !== line.key))}
                              title="Remove line"
                              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={(event) => {
                  const anchor = event.currentTarget;
                  setPicker((current) => (current?.lineKey === null ? null : {anchor, lineKey: null}));
                }}
                aria-haspopup="dialog"
                aria-expanded={picker?.lineKey === null}
                className={`${subtleButtonClass} mt-3 inline-flex items-center gap-1.5`}
              >
                <Plus size={14} /> Add product
              </button>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                The picker lists the active catalog grouped by module, then seats, then billing-only products such as{' '}
                <span className="font-mono">cway_enhanced_support</span> grouped by name. Managed products are hidden until you switch on &ldquo;Show
                managed&rdquo; in the picker, because they lock the subscription to staff changes. &ldquo;Paste a product id&rdquo; reaches anything
                the list does not carry. The server checks the id against Stripe when the offer is made. Leave &ldquo;% off&rdquo; empty to bill the
                line at list price, and use 100 for a free line.
              </p>
              {picker && (
                <ProductPicker
                  anchor={picker.anchor}
                  groups={productGroups}
                  selectedId={lines.find((line) => line.key === picker.lineKey)?.productId}
                  takenIds={new Set(lines.filter((line) => line.key !== picker.lineKey).map((line) => line.productId.trim()))}
                  onSelect={pickProduct}
                  onPasteId={pasteProductId}
                  managedCount={managedCount}
                  showManaged={showManaged}
                  onShowManagedChange={setShowManaged}
                  onClose={closePicker}
                />
              )}
            </section>

            {/* Offer-level terms */}
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Terms</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Discount on everything (%)
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="any"
                    value={globalPercent}
                    onChange={(event) => setGlobalPercent(event.target.value)}
                    placeholder="—"
                    className={inputClass}
                  />
                </label>
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  …for how many months
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={globalMonths}
                    onChange={(event) => setGlobalMonths(event.target.value)}
                    placeholder="∞"
                    disabled={globalPercent.trim() === ''}
                    className={`${inputClass} disabled:opacity-50`}
                  />
                </label>
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Free trial (days)
                  <input
                    type="number"
                    min={MIN_TRIAL_DAYS}
                    max={MAX_TRIAL_DAYS}
                    step={1}
                    value={trialDays}
                    onChange={(event) => setTrialDays(event.target.value)}
                    placeholder={target ? 'Not on a running subscription' : 'None'}
                    disabled={!!target}
                    title={target ? 'Use a 100% discount for N months instead' : undefined}
                    className={`${inputClass} disabled:opacity-50`}
                  />
                  {target && <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">Use a 100% discount for N months instead.</span>}
                </label>
                <div>
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Payment terms (days)
                    <input
                      type="number"
                      min={MIN_PAYMENT_TERMS_DAYS}
                      max={MAX_PAYMENT_TERMS_DAYS}
                      step={1}
                      value={paymentTermsDays}
                      onChange={(event) => setPaymentTermsDays(event.target.value)}
                      placeholder="Paid by card"
                      title={`Empty means the customer pays by card. ${SUGGESTED_PAYMENT_TERMS_DAYS} is net ${SUGGESTED_PAYMENT_TERMS_DAYS}.`}
                      className={inputClass}
                    />
                  </label>
                  {!isInvoicedOffer && (
                    <button
                      type="button"
                      onClick={() => setPaymentTermsDays(String(SUGGESTED_PAYMENT_TERMS_DAYS))}
                      className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Use net {SUGGESTED_PAYMENT_TERMS_DAYS}
                    </button>
                  )}
                </div>
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Accept before
                  <input
                    type="date"
                    min={todayLocalISO()}
                    value={validUntil}
                    onChange={(event) => setValidUntil(event.target.value)}
                    className={inputClass}
                  />
                </label>
              </div>
              <label className="block mt-3 text-sm text-gray-700 dark:text-gray-300">
                Internal note
                <input
                  type="text"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="e.g. Agreed with Anna at the Q3 renewal call"
                  className={inputClass}
                />
              </label>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {target
                  ? 'A month count runs from the day the customer accepts, so a carried-over discount restarts then. '
                  : 'A month count runs from acceptance and the trial is inside it — 30 trial days plus “12 months” leaves roughly 11 discounted paid months. '}
                Leave it empty for a discount that lasts as long as the subscription does. The note stays internal; the customer never sees it.
              </p>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {isInvoicedOffer ? (
                  <>
                    Invoiced with {termsDays}-day terms — the customer enters company, address and VAT id when accepting; no Stripe checkout, no card.
                    Paid by bank transfer or card on the hosted invoice. Terms are a credit decision: there is no self-service way to get them, so
                    only offer them to a customer sales has agreed them with.
                  </>
                ) : (
                  <>
                    Leave payment terms empty for the usual card deal. Fill them in for a customer who has to be invoiced and pays by bank transfer —
                    net {SUGGESTED_PAYMENT_TERMS_DAYS} is the usual choice, and {MIN_PAYMENT_TERMS_DAYS}–{MAX_PAYMENT_TERMS_DAYS} days is what the
                    server accepts.
                  </>
                )}
              </p>
            </section>

            {/* Preview */}
            <section className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">What the customer sees</h3>
              {previewLines.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-300">Pick a product to preview the deal.</p>
              ) : (
                <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                  {previewLines.map((line, index) => {
                    const deal = describeOfferDiscount(line.discount);
                    const listAmount = lineListAmount(line);
                    return (
                      <li key={`${line.productId}-${index}`} className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-medium">{line.product?.name || line.productId}</span>
                        {line.quantity > 1 && <span className="text-gray-500 dark:text-gray-400">× {line.quantity}</span>}
                        <span className="text-gray-500 dark:text-gray-400">
                          {listAmount !== null && line.product ? formatMoney(listAmount, line.product.price.currency) : 'price set by Stripe'}
                        </span>
                        {deal && <span className="text-green-700 dark:text-green-400">{deal}</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
              {globalDiscountText && <p className="text-sm text-green-700 dark:text-green-400">{globalDiscountText} on the whole subscription</p>}
              {parseField(trialDays) !== null && <p className="text-sm text-gray-700 dark:text-gray-200">First {parseField(trialDays)} days free</p>}
              {isInvoicedOffer && (
                <p className="text-sm text-gray-700 dark:text-gray-200">
                  Invoiced · net {termsDays} — no card, no Stripe checkout. Each invoice is payable within {termsDays} days by bank transfer or card
                  on the hosted invoice
                  {parseField(trialDays) !== null ? ', and the first one comes after the trial' : ''}.
                </p>
              )}
              {switchesToInvoicing && (
                <p className="flex items-start gap-1 text-sm text-amber-700 dark:text-amber-400">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  Switches the subscription to invoicing, net {termsDays}: the card on file stops being charged. Only the Stripe Dashboard can put it
                  back on card.
                </p>
              )}
              {isInvoicedOffer && target && (
                <p className="flex items-start gap-1 text-sm text-amber-700 dark:text-amber-400">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  If the person who accepts is not the Stripe customer this subscription is billed to — a colleague set it up — accepting starts an
                  invoiced subscription of their own and cancels this one at once, with no refund of a prepaid remainder. Check that before offering
                  terms to a customer paying annually up front.
                </p>
              )}
              {target && !target.autoRenewal && previewLines.length > 0 && (
                <p className="text-sm text-gray-700 dark:text-gray-200">Accepting also resumes automatic renewal.</p>
              )}
              {seatWarning && (
                <p className="flex items-start gap-1 text-sm text-amber-700 dark:text-amber-400">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  {seatWarning}; the owners get a mail listing who.
                </p>
              )}
              {totals.length > 0 && (
                <p className="text-sm text-gray-900 dark:text-gray-100 pt-2 border-t border-gray-200 dark:border-gray-600">
                  First month, before tax: <span className="font-semibold">{formatOfferTotals(totals)}</span>
                  {totals[0].incomplete && <span className="text-gray-500 dark:text-gray-400"> — plus the products priced only in Stripe</span>}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Prices are ex-VAT; Stripe adds tax on the invoice. Time-limited discounts fall away later, so the total above is the first month, not
                the lifetime price.
                {target && ' The customer also sees what the rest of the current period costs today, prorated by Stripe, before they accept.'}
                {isInvoicedOffer &&
                  ' The customer’s VAT id decides the tax: with a valid EU or GB one the invoice is reverse-charged, without it local VAT is added.'}
              </p>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button type="button" onClick={onClose} disabled={creating} className={subtleButtonClass}>
                Cancel
              </button>
              <button type="submit" disabled={creating} className={primaryButtonClass}>
                {creating ? 'Making offer…' : 'Make offer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
